"use client";
import React, { useState, useEffect, useMemo } from "react";
import { Button } from "components/ui/button";
import { EventsFilters } from "./events-filters";
import EventCard from "components/reusables/event-card";
import FeatureEventCard from "components/reusables/featured-event-card";
import { useSearchParams } from "next/navigation";
import useEventsStore from "~/stores/events-store";
import { SearchBar } from "./search-bar";

type EventType = {
  id: string | number;
  featured?: boolean;
  type?: string;
  title: string;
  date?: string;
  publishedAt?: string;
  keywords?: string[];
};

interface EventsGridProps {
  title: string;
  fixedEventsData: EventType[];
}

export const EventsGrid: React.FC<EventsGridProps> = ({
  title,
  fixedEventsData,
}) => {
  const [visibleItems, setVisibleItems] = useState(5); // Start with 5 items
  const searchParams = useSearchParams();

  const activeFilters = useEventsStore((state) => state.filters);
  const sortingOption = useEventsStore((state) => state.sortingOption);
  const setFilter = useEventsStore((state) => state.setFilter);

  const featuredEvent = fixedEventsData.filter(
    (event: EventType) => event.featured,
  );
  // Filter data based on active filters
  const filteredData = useMemo(() => {
    const search = searchParams.get("search");
    let data = fixedEventsData.filter((event: EventType) => {
      const matchesType =
        activeFilters.type.length === 0 ||
        (event?.type && activeFilters.type.includes(event?.type));

      const keywordMatch =
        event.keywords &&
        event.keywords.some(
          (keyword: string) =>
            keyword.toLowerCase().includes(search?.toLowerCase() || "") ||
            keyword
              .toLowerCase()
              .includes(activeFilters.searchQuery.trim().toLowerCase()),
        );

      const matchesSearchQuery =
        !search ||
        event.title.toLowerCase().includes(search.toLowerCase()) ||
        keywordMatch ||
        activeFilters.searchQuery.trim() === "" ||
        event.title
          .toLowerCase()
          .includes(activeFilters.searchQuery.trim().toLowerCase());

      return matchesType && matchesSearchQuery && !event.featured;
    });

    // Sort based on sortingOptions
    data = [...data].sort((a: EventType, b: EventType) => {
      if (sortingOption == "Upcoming events") {
        const dateA = new Date(a.date ?? "").getTime();
        const dateB = new Date(b.date ?? "").getTime();

        return title == "Upcoming Events" ? dateA - dateB : dateB - dateA;
      } else {
        const dateA = new Date(a.publishedAt ?? "").getTime();
        const dateB = new Date(b.publishedAt ?? "").getTime();

        return dateB - dateA;
      }
    });

    return data;
  }, [fixedEventsData, activeFilters, sortingOption, searchParams]);

    useEffect(() => {
      const type = searchParams.get("type");

      if (type) {
        setFilter("type", type);
      }
      
    }, [searchParams, setFilter]);
    
  return (
    <div className="container grid grid-cols-1 gap-6 pt-6 md:gap-12 md:pt-12 xl:grid-cols-12">
      <div className="col-span-1 flex flex-col text-dark-gray md:mt-16 xl:col-span-3">
        <SearchBar />
        <div className="max-xl:hidden">
          <EventsFilters />
        </div>
      </div>
      <div className="col-span-1 xl:col-span-9">
        <div className="flex items-center justify-between">
          <h3 className="font-h3">{title}</h3>
          {filteredData.length > 0 && (
            <p className="font-p">
              Showing{" "}
              {Math.min(
                filteredData.length + featuredEvent.length,
                visibleItems,
              )}{" "}
              of {filteredData.length + featuredEvent.length} results
            </p>
          )}
        </div>
        <div className="grid grid-cols-1 gap-6 pt-8 md:grid-cols-2 md:pt-12">
          {filteredData.length > 0 ? (
            filteredData
              .slice(0, visibleItems - featuredEvent.length)
              .map((event: EventType, i: number) => (
                <React.Fragment key={`event-fragment-${event.id}-${i}`}>
                  <EventCard
                    event={event}
                    passedEvent={title === "Past Events"}
                  />
                  {i === 1 && featuredEvent.length > 0 && (
                    <FeatureEventCard
                      event={featuredEvent[0]}
                      passedEvent={title === "Past Events"}
                    />
                  )}
                </React.Fragment>
              ))
          ) : (
            <>
              <p className="font-h4 col-span-1 md:col-span-2">
                No events found matching your criteria.
              </p>
              {featuredEvent.length > 0 && (
                <FeatureEventCard key={`featured`} event={featuredEvent[0]} />
              )}
            </>
          )}
          {filteredData.length + featuredEvent.length >
            Math.min(
              filteredData.length + featuredEvent.length,
              visibleItems,
            ) && (
            <div className="col-span-1 flex  w-full justify-center p-12 md:col-span-2">
              <Button
                aria-label="Load more"
                variant="outline"
                className="border-black hover:bg-opacity-10"
                size={"lg"}
                onClick={() => setVisibleItems((prev) => prev + 5)}
              >
                Load more
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
